import type {
  StickyMessage,
  StickyMessageState,
} from '@pin/schemas/stickyMessage';
import {
  catchError,
  firstValueFrom,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  timeout,
  timer,
} from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { withIdToken } from '../firebase/auth';
import {
  checkNoContentSuccess,
  HttpError,
  retryWithBackoff,
} from '../utils/rxjs.http';

const fetchStickyMessages$ = withIdToken((token) =>
  fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/sticky-message`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    selector: (r): Promise<StickyMessageState> => {
      if (!r.ok) {
        throw new HttpError(r);
      }
      return r.json();
    },
  }).pipe(timeout(6_000), retryWithBackoff(3))
);

/** Fetches the sticky messages again, as admin-api asks on every change */
export const stickyMessageSubject$ = new Subject<void>();
// A failed fetch is tried again rather than ending the stream, which would
// leave the panel stale with nothing to say so
export const stickyMessages$ = stickyMessageSubject$.pipe(
  startWith(0),
  switchMap(() =>
    fetchStickyMessages$.pipe(
      catchError((_err: unknown, source) =>
        timer(5_000).pipe(switchMap(() => source))
      )
    )
  ),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const createStickyMessage = async (payload: {
  text: string;
  issuedBy: string;
}) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}/admin/sticky-message`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify(payload),
      }).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const editStickyMessage = async (
  id: string,
  payload: {
    text: string;
    issuedBy: string;
  }
) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(
        `${import.meta.env.VITE_ADMIN_API_URL}/admin/sticky-message/${id}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          method: 'PATCH',
          body: JSON.stringify(payload),
        }
      ).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const deleteStickyMessage = async (id: string) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(
        `${import.meta.env.VITE_ADMIN_API_URL}/admin/sticky-message/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          method: 'DELETE',
        }
      ).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const showStickyMessage = async (payload: StickyMessage) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(
        `${import.meta.env.VITE_ADMIN_API_URL}/admin/sticky-message/live`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          method: 'POST',
          body: JSON.stringify(payload),
        }
      ).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const hideStickyMessage = async () => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(
        `${import.meta.env.VITE_ADMIN_API_URL}/admin/sticky-message/inactive`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          method: 'POST',
        }
      ).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};
